import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { cn } from '../lib/utils';

// Private usage dashboard. Reads ONE aggregate doc written nightly by the
// nightlyUsageRollup Cloud Function (plus a small slice of dated history for the
// trend column). It never queries teams/users directly — firestore.rules scope
// those per-coach, and the whole point of the rollup is that the aggregation
// happens server-side with the Admin SDK.
//
// Colors are arbitrary hex on purpose: the .light theme remaps Tailwind's named
// colors, so bg-ink / text-zinc-* render wrong here.

const SNAPSHOT_PATH = ['adminMetrics', 'usageSnapshot'] as const;

type Snap = any;

const Delta: React.FC<{ now: number; before?: number }> = ({ now, before }) => {
  if (before === undefined || before === null) return null;
  const d = now - before;
  if (d === 0) return <span className="text-[10px] font-bold text-ink-2">—</span>;
  return (
    <span className={cn('text-[10px] font-black', d > 0 ? 'text-accent-ink' : 'text-gold-ink')}>
      {d > 0 ? `+${d}` : d}
    </span>
  );
};

const Stat: React.FC<{ label: string; value: number; before?: number; indent?: boolean }> = ({
  label, value, before, indent,
}) => (
  <div className={cn('flex items-baseline justify-between py-2 border-b border-hairline last:border-0', indent && 'pl-4')}>
    <span className={cn('text-xs font-bold', indent ? 'text-ink-2' : 'text-ink-2')}>{label}</span>
    <span className="flex items-baseline gap-2">
      <span className="text-sm font-black text-ink tabular-nums">{value}</span>
      <Delta now={value} before={before} />
    </span>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-5">
    <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">{title}</p>
    <div className="bg-raised border border-hairline rounded-2xl px-4">{children}</div>
  </div>
);

// Renders a { key: count } map as a sorted chip row (style / age group / org mix).
const Mix: React.FC<{ title: string; data: Record<string, number> | undefined }> = ({ title, data }) => {
  const entries = Object.entries(data ?? {}).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return (
    <div className="mb-4">
      <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-2">{title}</p>
      <div className="flex gap-1.5 flex-wrap">
        {entries.map(([k, v]) => (
          <span key={k} className="px-2.5 py-1 rounded-xl bg-hairline text-ink-2 text-[10px] font-black">
            {k} · {v}
          </span>
        ))}
      </div>
    </div>
  );
};

export const AdminUsagePanel: React.FC = () => {
  const [snap, setSnap] = useState<Snap | null>(null);
  const [prior, setPrior] = useState<Snap | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, ...SNAPSHOT_PATH),
      (s) => { setSnap(s.exists() ? s.data() : null); setLoading(false); },
      () => { setError(true); setLoading(false); }
    );
    return unsub;
  }, []);

  // Pull the ~8 most recent dated docs and pick the one closest to 7 days back,
  // so the trend column survives a night where the sweep didn't run.
  useEffect(() => {
    (async () => {
      try {
        const q = query(
          collection(db, ...SNAPSHOT_PATH, 'history'),
          orderBy('generatedAt', 'desc'),
          limit(8)
        );
        const docs = (await getDocs(q)).docs.map(d => d.data());
        const target = Date.now() - 7 * 24 * 60 * 60 * 1000;
        let best: Snap | null = null;
        let bestGap = Infinity;
        for (const d of docs) {
          const gap = Math.abs(new Date(d.generatedAt).getTime() - target);
          if (gap < bestGap) { bestGap = gap; best = d; }
        }
        setPrior(best);
      } catch { /* trend column is optional */ }
    })();
  }, []);

  if (loading) return null;

  if (error || !snap) {
    return (
      <div className="mt-10 border-t border-hairline pt-8">
        <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-3">Admin: Usage</p>
        <p className="text-ink-2 text-xs font-bold py-4">
          No snapshot yet. The nightly rollup writes the first one at 8:15am ET.
        </p>
      </div>
    );
  }

  const c = snap.coaches ?? {};
  const t = snap.teams ?? {};
  const r = snap.revenue ?? {};
  const pc = prior?.coaches ?? {};
  const pt = prior?.teams ?? {};
  const pr = prior?.revenue ?? {};
  const p = snap.passHolders ?? {};
  const pp = prior?.passHolders ?? {};

  return (
    <div className="mt-10 border-t border-hairline pt-8">
      <div className="flex items-baseline justify-between mb-4">
        <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Admin: Usage</p>
        <p className="text-[10px] font-bold text-ink-2">
          {String(snap.generatedAt ?? '').slice(0, 10)}
          {prior ? ' · vs 7d ago' : ''}
        </p>
      </div>

      <Section title="Coaches">
        <Stat label="Total coaches" value={c.total ?? 0} before={pc.total} />
        <Stat label="Signed up (7d)" value={c.new7 ?? 0} before={pc.new7} />
        <Stat label="Signed up (30d)" value={c.new30 ?? 0} before={pc.new30} />
        <Stat label="Demo leftovers" value={c.demoPhantoms ?? 0} before={pc.demoPhantoms} indent />
        <Stat label="Test accounts" value={c.testAccounts ?? 0} before={pc.testAccounts} indent />
      </Section>

      <Section title="Activation funnel">
        <Stat label="Teams created" value={t.total ?? 0} before={pt.total} />
        <Stat label="…with a roster" value={t.withPlayers ?? 0} before={pt.withPlayers} indent />
        <Stat label="…ran a game" value={t.withGame ?? 0} before={pt.withGame} indent />
        <Stat label="…ran a 2nd game" value={t.withTwoGames ?? 0} before={pt.withTwoGames} indent />
        <Stat label="…imported a game" value={t.withImportedGame ?? 0} before={pt.withImportedGame} indent />
        <Stat label="…imported 2+ games" value={t.withTwoImportedGames ?? 0} before={pt.withTwoImportedGames} indent />
        <Stat label="Scorebook PDF" value={t.withScorebookImport ?? 0} before={pt.withScorebookImport} indent />
        <Stat label="Stats CSV" value={t.withImportedStats ?? 0} before={pt.withImportedStats} indent />
      </Section>

      <Section title="Activity">
        <Stat label="Active teams (7d)" value={t.active7 ?? 0} before={pt.active7} />
        <Stat label="Active teams (30d)" value={t.active30 ?? 0} before={pt.active30} />
        <Stat label="Total games" value={snap.totals?.games ?? 0} before={prior?.totals?.games} />
        <Stat label="Total players" value={snap.totals?.players ?? 0} before={prior?.totals?.players} />
      </Section>

      <Section title="Season Pass holders">
        <Stat label="Pass holders" value={p.total ?? 0} before={pp.total} />
        <Stat label="…has a team" value={p.withTeam ?? 0} before={pp.withTeam} indent />
        <Stat label="…ran a game" value={p.withGame ?? 0} before={pp.withGame} indent />
        <Stat label="…imported a game" value={p.withImport ?? 0} before={pp.withImport} indent />
        <Stat label="…active (30d)" value={p.active30 ?? 0} before={pp.active30} indent />
        <Stat label="…active (7d)" value={p.active7 ?? 0} before={pp.active7} indent />
      </Section>

      <Section title="Revenue">
        <Stat label="Stripe: paying" value={r.stripePaying ?? 0} before={pr.stripePaying} />
        <Stat label="Stripe: in trial" value={r.stripeTrialing ?? 0} before={pr.stripeTrialing} indent />
        <Stat label="Stripe: comped ($0)" value={r.stripeComped ?? 0} before={pr.stripeComped} indent />
        <Stat label="Apple: paying" value={r.iapPaying ?? 0} before={pr.iapPaying} />
        <Stat label="Apple: in trial" value={r.iapTrialing ?? 0} before={pr.iapTrialing} indent />
        <Stat label="Founding comps (no expiry)" value={r.grantedElite ?? 0} before={pr.grantedElite} />
        <Stat label="Season Passes claimed" value={r.seasonPasses ?? 0} before={pr.seasonPasses} />
      </Section>

      <Mix title="Style of play" data={snap.mix?.byStyle} />
      <Mix title="Age group" data={snap.mix?.byAgeGroup} />
      <Mix title="League org" data={snap.mix?.byOrg} />
      <Mix title="Lineup format (teams)" data={snap.lineupFormat?.teamsByStyle} />
      <Mix title="Lineup format (completed games)" data={snap.lineupFormat?.completedGamesByStyle} />

      <Section title="Lineup format signals">
        <Stat label="Continuous, used 9/10 once" value={snap.lineupFormat?.teamsWithPastNineOrTen ?? 0} before={prior?.lineupFormat?.teamsWithPastNineOrTen} />
        <Stat label="GC games: fielded, no PA" value={snap.lineupFormat?.gcGamesFieldedNotBatted ?? 0} before={prior?.lineupFormat?.gcGamesFieldedNotBatted} />
        <Stat label="…across teams" value={snap.lineupFormat?.gcTeamsFieldedNotBatted ?? 0} before={prior?.lineupFormat?.gcTeamsFieldedNotBatted} indent />
      </Section>

      <Section title="Support">
        <Stat label="Open tickets" value={snap.support?.openTickets ?? 0} before={prior?.support?.openTickets} />
      </Section>
    </div>
  );
};
